import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  cancelTimeOff,
  decideTimeOff,
  fetchTimeOff,
  requestTimeOff,
} from "../../../business/redux/reducers/mcOpsDashboard/actions";
import { Icon } from "../icons";
import { addDays, fmtDate, plural, todayStr } from "../lib/format";
import { isHourlyJob } from "../lib/hourly";
import { useMcOps } from "../lib/McOpsContext";
import {
  approvalWarnings,
  hasStorage,
  isOut,
  myDueJobs,
  rangeLabel,
  requestBody,
  requestError,
} from "../lib/buddy";

/**
 * Roadway Buddy (Asana 1219929429708587, design of 2026-10-29): request time
 * off with a buddy, hand off every job with something due while out, and the
 * manager's approval. Covered jobs show on the buddy's boards while it runs.
 */

const STATUS = {
  pending: ["var(--warning-light)", "var(--warning)", "Pending approval"],
  approved: ["var(--success-light)", "var(--success)", "Approved"],
  declined: ["var(--danger-light)", "var(--danger)", "Declined"],
  cancelled: ["var(--ink-100)", "var(--ink-500)", "Cancelled"],
};

const StatusBadge = ({ status }) => {
  const [bg, color, label] = STATUS[status] || STATUS.cancelled;
  return (
    <span className="badge" style={{ background: bg, color }}>
      {label}
    </span>
  );
};

const money = (n) => (typeof n === "number" ? "$" + Math.round(n).toLocaleString("en-US") : "—");
const short = (d) => fmtDate(d).replace(/, \d{4}/, "");
const first = (s) => (s || "").split(" ")[0];

/** One handed-off job: what is due, who covers it and the note. */
const HandoffJob = ({ job, mainBuddy, onOpen, children }) => (
  <div className="liab-block">
    <div className="liab-top" style={{ marginBottom: 6 }}>
      <div className="liab-q">
        <div>
          {job.clientName} — {job.zohoId}
        </div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", margin: "4px 0" }}>
          <span className={`badge ${job.moveType === "ld" ? "ld" : "local"}`} style={{ fontSize: 9.5, padding: "2px 7px" }}>
            {job.moveType === "ld" ? "Long Distance" : "Local"}
          </span>
          {job.hourly ? (
            <span className="badge" style={{ background: "var(--warning-light)", color: "var(--warning)", fontSize: 9.5, padding: "2px 7px" }}>
              Hourly
            </span>
          ) : null}
          {job.storage ? (
            <span className="badge storage" style={{ fontSize: 9.5, padding: "2px 7px" }}>
              Storage
            </span>
          ) : null}
          {job.buddy && job.buddy !== mainBuddy ? (
            <span className="badge plain" style={{ fontSize: 9.5, padding: "2px 7px" }}>
              Covered by {job.buddy}
            </span>
          ) : null}
        </div>
        <div className="liab-sub" style={{ color: "var(--ink-700)", fontWeight: 600, marginBottom: 4 }}>
          {money(job.price)} · Source: {job.source || "—"}
        </div>
        <div className="liab-sub">
          {(job.due || []).map((i) => (
            <div key={i.date + i.label}>
              {short(i.date)} · {i.label}
            </div>
          ))}
        </div>
      </div>
      {onOpen ? (
        <button type="button" className="btn small" onClick={onOpen}>
          <Icon name="note" />
          Open
        </button>
      ) : null}
    </div>
    {children}
  </div>
);

const SavedNote = ({ note }) => (
  <div className="liab-sub" style={{ color: "var(--ink-700)" }}>
    {note ? "“" + note + "”" : "No handoff note."}
  </div>
);

/** The manager's queue: approve or decline, with what to watch for. */
const ManagerQueue = ({ requests, onDecide, openLead }) => {
  const [notes, setNotes] = useState({});
  const [errors, setErrors] = useState({});
  const t = todayStr();
  const pending = requests.filter((r) => r.status === "pending");
  const approved = requests.filter((r) => r.status === "approved" && r.end >= t);

  const decide = async (r, decision) => {
    const err = await onDecide(r.id, decision, notes[r.id] || "");
    setErrors((e) => ({ ...e, [r.id]: err }));
  };

  return (
    <>
      <div className="section-label" style={{ marginTop: 0 }}>
        <Icon name="clock" />
        Waiting for your approval <span className="meta">({pending.length})</span>
      </div>
      {pending.length ? (
        pending.map((r) => {
          const clash = requests.filter((o) => o.id !== r.id && o.status === "approved" && o.start <= r.end && o.end >= r.start);
          const byBuddy = {};
          (r.jobs || []).forEach((j) => {
            const name = j.buddy || r.buddy;
            byBuddy[name] = (byBuddy[name] || 0) + 1;
          });
          const n = (r.jobs || []).length;
          return (
            <div className="liab-card" key={r.id}>
              <div className="liab-row" style={{ alignItems: "flex-start" }}>
                <div className="liab-q">
                  <div>
                    {r.mc} · {rangeLabel(r)}
                  </div>
                  <div className="liab-sub">
                    Covered by:{" "}
                    {Object.keys(byBuddy).length
                      ? Object.keys(byBuddy).map((b) => `${b} (${byBuddy[b]})`).join(", ")
                      : r.buddy}{" "}
                    · {n} {plural(n, "job")} with something due
                    {r.reason ? " · " + r.reason : ""}
                  </div>
                </div>
                <StatusBadge status={r.status} />
              </div>
              {approvalWarnings(r, requests).map((w) => (
                <div
                  key={w.text}
                  className="liab-row"
                  style={{ color: `var(--${w.cls})`, fontSize: 12, fontWeight: 700, justifyContent: "flex-start" }}
                >
                  <Icon name="alert" />
                  {w.text}
                </div>
              ))}
              {clash.length ? (
                <div className="liab-row" style={{ fontSize: 12, color: "var(--ink-500)" }}>
                  Also out then: {clash.map((o) => `${o.mc} (${rangeLabel(o)})`).join(", ")}
                </div>
              ) : null}
              {(r.jobs || []).length ? (
                r.jobs.map((j) => (
                  <HandoffJob key={j.leadId} job={j} mainBuddy={r.buddy} onOpen={openLead(j.leadId)}>
                    <SavedNote note={j.note} />
                  </HandoffJob>
                ))
              ) : (
                <div className="liab-row">
                  <div className="liab-sub">No jobs have anything due in this window, so there is nothing to hand off.</div>
                </div>
              )}
              <div className="liab-block">
                <textarea
                  rows={2}
                  aria-label={`Note to ${first(r.mc)}`}
                  placeholder={`Note to ${first(r.mc)} (optional)`}
                  value={notes[r.id] || ""}
                  onChange={(e) => setNotes((x) => ({ ...x, [r.id]: e.target.value }))}
                />
                {errors[r.id] ? (
                  <div role="alert" style={{ color: "var(--danger)", fontSize: 11.5, fontWeight: 700, marginTop: 6 }}>
                    {errors[r.id]}
                  </div>
                ) : null}
                <div className="toolbar-row">
                  <button type="button" className="btn success small" onClick={() => decide(r, "approve")}>
                    <Icon name="check" />
                    Approve
                  </button>
                  <button type="button" className="btn danger small" onClick={() => decide(r, "decline")}>
                    <Icon name="x" />
                    Decline
                  </button>
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="liab-card">
          <div className="liab-row">
            <div className="liab-sub">Nothing waiting.</div>
          </div>
        </div>
      )}
      <div className="section-label">
        <Icon name="clock" />
        Approved time off
      </div>
      <div className="liab-card">
        {approved.length ? (
          approved.map((r) => (
            <div className="liab-row" key={r.id}>
              <div className="liab-q">
                <div>
                  {r.mc} · {rangeLabel(r)}
                </div>
                <div className="liab-sub">Buddy: {r.buddy}</div>
              </div>
              <StatusBadge status={r.status} />
            </div>
          ))
        ) : (
          <div className="liab-row">
            <div className="liab-sub">No upcoming approved time off.</div>
          </div>
        )}
      </div>
    </>
  );
};

const emptyDraft = () => ({
  start: addDays(todayStr(), 3),
  end: addDays(todayStr(), 5),
  buddyId: 0,
  reason: "",
  notes: {},
  jobBuddy: {},
});

export const RoadwayBuddyPage = () => {
  const dispatch = useDispatch();
  const { state, openJob } = useMcOps();
  const meta = useSelector((s) => s.mcOpsDashboard?.meta) || {};
  const timeOff = meta.timeOff || {};
  const requests = timeOff.requests || [];
  const me = timeOff.me || {};
  const [draft, setDraft] = useState(emptyDraft);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState({ due: true, cover: true });
  const t = todayStr();

  useEffect(() => {
    dispatch(fetchTimeOff());
  }, [dispatch]);

  const valid = draft.start && draft.end && draft.end >= draft.start;
  const due = valid ? myDueJobs(state.jobs, meta.mc?.zohoId, draft.start, draft.end) : [];
  const team = (timeOff.team || []).filter((m) => m.id !== me.id);
  const outIn = (id) => valid && isOut(requests, id, draft.start, draft.end);
  const mine = requests.filter((r) => r.mcId === me.id);
  const covering = requests.filter(
    (r) => r.status === "approved" && r.end >= t && r.mcId !== me.id && (r.jobs || []).some((j) => (j.buddyId || r.buddyId) === me.id),
  );
  const openLead = (leadId) =>
    state.jobs.some((j) => Number(j.leadId) === Number(leadId)) ? () => openJob("lead-" + leadId) : null;

  const set = (patch) => {
    setError("");
    setDraft((d) => {
      const next = { ...d, ...patch };
      if (patch.start && next.end < next.start) next.end = next.start;
      // A buddy who turns out to be away in the new dates is cleared.
      const ok = (id) => id && !(next.start && next.end && isOut(requests, id, next.start, next.end));
      if (!ok(next.buddyId)) next.buddyId = 0;
      const jobBuddy = {};
      Object.keys(next.jobBuddy).forEach((k) => {
        if (ok(next.jobBuddy[k]) && next.jobBuddy[k] !== next.buddyId) jobBuddy[k] = next.jobBuddy[k];
      });
      next.jobBuddy = jobBuddy;
      return next;
    });
  };

  const submit = async () => {
    const err = requestError(draft, due, t);
    if (err) return setError(err);
    setSending(true);
    const res = await dispatch(requestTimeOff(requestBody(draft, due)));
    setSending(false);
    if (res) return setError(res);
    setDraft(emptyDraft());
  };

  const nameOf = (id) => (team.find((m) => m.id === id) || {}).name || "";
  const buddyOptions = (exclude) =>
    team
      .filter((m) => m.id !== exclude)
      .map((m) => {
        const out = outIn(m.id);
        return (
          <option key={m.id} value={m.id} disabled={!!out}>
            {m.name}
            {out ? " — also out" : ""}
          </option>
        );
      });

  return (
    <div className="page active">
      <div className="page-title-row">
        <div>
          <h2 className="page-title">Roadway Buddy</h2>
          <div className="page-desc">
            Pick a buddy for each absence. Only jobs with something due while you are out are covered, and the manager
            approves before it takes effect.
          </div>
        </div>
      </div>

      {timeOff.error && !timeOff.requests ? (
        <div className="liab-card">
          <div className="liab-row">
            <div className="liab-sub">Time off could not be loaded. Refresh the page to try again.</div>
          </div>
        </div>
      ) : null}

      {timeOff.canApprove ? (
        <ManagerQueue
          requests={requests}
          openLead={openLead}
          onDecide={(id, decision, note) => dispatch(decideTimeOff(id, decision, note))}
        />
      ) : null}

      <div className="section-label" style={timeOff.canApprove ? undefined : { marginTop: 0 }}>
        <Icon name="clock" />
        Request time off
      </div>
      <div className="liab-card">
        <div className="liab-block">
          <div className="field-row" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
            <div className="field">
              <label htmlFor="ooo-start">First day out</label>
              <input id="ooo-start" type="date" min={t} value={draft.start} onChange={(e) => set({ start: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="ooo-end">Last day out</label>
              <input
                id="ooo-end"
                type="date"
                min={draft.start || t}
                value={draft.end}
                onChange={(e) => set({ end: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="ooo-buddy">Buddy for this absence</label>
              <select id="ooo-buddy" value={draft.buddyId || ""} onChange={(e) => set({ buddyId: Number(e.target.value) || 0 })}>
                <option value="">Choose a buddy</option>
                {buddyOptions()}
              </select>
            </div>
          </div>
          <div className="field">
            <label htmlFor="ooo-reason">Reason (optional)</label>
            <input
              id="ooo-reason"
              type="text"
              value={draft.reason}
              placeholder="Vacation, appointment, ..."
              onChange={(e) => set({ reason: e.target.value })}
            />
          </div>
        </div>
        <div
          className="liab-block"
          style={{ background: "var(--ink-50)", cursor: "pointer" }}
          onClick={() => setOpen((o) => ({ ...o, due: !o.due }))}
        >
          <div className="liab-top">
            <div className="liab-q">
              <div>
                Jobs with something due while you are out{" "}
                <span className="badge plain" style={{ marginLeft: 4 }}>
                  {due.length}
                </span>
              </div>
              <div className="liab-sub">Only these are covered. Add a handoff note to each so your buddy can pick it up cold.</div>
            </div>
            <Icon name="chevronDown" />
          </div>
        </div>
        {open.due ? (
          !valid ? (
            <div className="liab-row">
              <div className="liab-sub">Pick valid dates to see the jobs.</div>
            </div>
          ) : due.length ? (
            due.map(({ job, items }) => (
              <HandoffJob
                key={job.id}
                job={{
                  ...job,
                  due: items,
                  buddy: nameOf(draft.jobBuddy[job.id]),
                  hourly: isHourlyJob(job),
                  storage: hasStorage(job),
                }}
                mainBuddy={nameOf(draft.buddyId)}
                onOpen={() => openJob(job.id)}
              >
                <div className="field" style={{ margin: "8px 0 6px" }}>
                  <label htmlFor={`ooo-jb-${job.id}`}>Covered by</label>
                  <select
                    id={`ooo-jb-${job.id}`}
                    value={draft.jobBuddy[job.id] || ""}
                    onChange={(e) => set({ jobBuddy: { ...draft.jobBuddy, [job.id]: Number(e.target.value) || 0 } })}
                  >
                    <option value="">{(nameOf(draft.buddyId) || "Main buddy") + " (main buddy)"}</option>
                    {buddyOptions(draft.buddyId)}
                  </select>
                </div>
                <textarea
                  rows={2}
                  aria-label={`Handoff note for ${job.clientName}`}
                  placeholder={`Handoff note for ${job.clientName}: what the buddy needs to know (required)`}
                  value={draft.notes[job.id] || ""}
                  onChange={(e) => set({ notes: { ...draft.notes, [job.id]: e.target.value } })}
                />
              </HandoffJob>
            ))
          ) : (
            <div className="liab-row">
              <div className="liab-sub">No jobs have anything due in this window, so there is nothing to hand off.</div>
            </div>
          )
        ) : null}
        <div className="liab-block">
          {error ? (
            <div role="alert" style={{ color: "var(--danger)", fontSize: 11.5, fontWeight: 700, marginBottom: 6 }}>
              {error}
            </div>
          ) : null}
          <div className="toolbar-row">
            <button type="button" className="btn primary small" disabled={sending} onClick={submit}>
              <Icon name="check" />
              Submit for manager approval
            </button>
          </div>
        </div>
      </div>

      {covering.length ? (
        <>
          <div
            className="section-label"
            style={{ cursor: "pointer", justifyContent: "space-between" }}
            onClick={() => setOpen((o) => ({ ...o, cover: !o.cover }))}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Icon name="clock" />
              Jobs you are covering{" "}
              <span className="badge plain" style={{ marginLeft: 4 }}>
                {covering.length} {plural(covering.length, "absence", "absences")}
              </span>
            </span>
            <Icon name="chevronDown" />
          </div>
          {open.cover
            ? covering.map((r) => (
                <div className="liab-card" key={r.id}>
                  <div className="liab-row">
                    <div className="liab-q">
                      <div>
                        For {r.mc} · {rangeLabel(r)}
                      </div>
                      <div className="liab-sub">Their jobs return to them automatically after {short(r.end)}.</div>
                    </div>
                  </div>
                  {r.jobs
                    .filter((j) => (j.buddyId || r.buddyId) === me.id)
                    .map((j) => (
                      <HandoffJob key={j.leadId} job={j} mainBuddy={r.buddy} onOpen={openLead(j.leadId)}>
                        <SavedNote note={j.note} />
                      </HandoffJob>
                    ))}
                </div>
              ))
            : null}
        </>
      ) : null}

      <div className="section-label">
        <Icon name="clock" />
        My requests
      </div>
      <div className="liab-card">
        {mine.length ? (
          mine.map((r) => {
            const others = (r.jobs || []).filter((j) => j.buddyId && j.buddyId !== r.buddyId).length;
            return (
              <div className="liab-row" key={r.id} style={{ alignItems: "flex-start" }}>
                <div className="liab-q">
                  <div>
                    {rangeLabel(r)} · Buddy: {r.buddy}
                    {others ? ` + ${others} ${plural(others, "job")} with a different MC` : ""}
                  </div>
                  <div className="liab-sub">
                    {r.reason ? r.reason + " · " : ""}
                    {r.managerNote ? "Manager: " + r.managerNote : r.status === "pending" ? "Waiting for the manager" : ""}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <StatusBadge status={r.status} />
                  {r.status === "pending" ? (
                    <button type="button" className="btn small" onClick={() => dispatch(cancelTimeOff(r.id))}>
                      Cancel
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })
        ) : (
          <div className="liab-row">
            <div className="liab-sub">No requests yet.</div>
          </div>
        )}
      </div>
    </div>
  );
};
